import { APP_INITIALIZER, enableProdMode, importProvidersFrom } from '@angular/core';
import { environment } from '@environment';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, HttpClient } from '@angular/common/http';
import { ApiInterceptor } from '@core/interceptor/api.interceptor';
import { ErrorInterceptor } from '@core/interceptor/error.interceptor';
import { RedirectGuard } from '@core/guards/redirect.guard';
import { CanActivateCommunitySettingsGuard } from '@core/guards/can-activate-community-settings.guard';
import { CanActivateWidgetSettingsGuard } from '@core/guards/can-activate-widget-settings.guard';
import { CanActivateSuperAdminSettingsGuard } from '@core/guards/can-activate-super-admin-settings.guard';
import { InitRedirectGuard } from '@core/guards/init-redirect.guard';
import { FacebookRedirectGuard } from '@core/guards/facebook-redirect.guard';
import { InstagramRedirectGuard } from '@core/guards/instagram-redirect.guard';
import { GoogleRedirectGuard } from '@core/guards/google-redirect.guard';
import { YoutubeRedirectGuard } from '@core/guards/youtube-redirect.guard';
import { TrustpilotRedirectGuard } from '@core/guards/trustpilot-redirect.guard';
import { TiktokRedirectGuard } from '@core/guards/tiktok-redirect.guard';
import { MicrosoftDynamicsRedirectGuard } from '@core/guards/microsoft-dynamics-redirect.guard';
import { TicketSourcePipe } from '@sb-tools/typescript/pipes/ticket-source.pipe';
import { EmailTemplatesTypesPipe } from '@shared/pipes/email-templates-types.pipe';
import { bootstrapApplication } from '@angular/platform-browser';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { UiSwitchModule } from 'ngx-ui-switch';
import { SimpleNotificationsModule } from 'angular2-notifications';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { NgxMaskModule } from 'ngx-mask';
import { AppComponent } from './app/app.component';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import packageJson from '../package.json';
import { provideRouter, withPreloading } from '@angular/router';
import { APP_ROUTES } from './app/app-routes';
import { CustomPreloadingStrategyService } from '@core/services/custom-preloading-strategy.service';
import { CurrentCategoryResolver } from '@community-settings/core/resolvers/current-category.resolver';
import { CategoriesResolver } from '@community-settings/core/resolvers/categories.resolver';
import { FieldRuleValidationTypeNamePipe } from '@community-settings/core/pipes/field-rule-validation-type-name.pipe';
import { UserService } from '@sb-tools/typescript/services/user.service';
import { SortPipe } from '@sb-table/pipes/sort.pipe';
import { provideAnimations } from '@angular/platform-browser/animations';
import { QuillHelperService } from '@sb-tools/typescript/services/quill/quill-helper.service';

const ngxUiSwitchConfig = {
    size: 'small',
    color: '#fed030',
    switchColor: '#FFFFFF',
    defaultBgColor: '#FFFFFF',
    defaultBoColor: '#dfdfdf',
    checkedLabel: '',
    uncheckedLabel: '',
};

function HttpLoaderFactory(http: HttpClient) {
    return new MultiTranslateHttpLoader(http, [
        { prefix: '/assets/i18n/', suffix: '.json?' + packageJson.version },
        { prefix: '/sb-tools/assets/i18n/', suffix: '.json?' + packageJson.version },
        { prefix: '/sb-modules/Frontend-Settings-Menu-Module/assets/i18n/', suffix: '.json?' + packageJson.version },
        { prefix: '/sb-modules/Frontend-Table-Module/assets/i18n/', suffix: '.json?' + packageJson.version },
        { prefix: '/sb-modules/Frontend-User-Avatar-Module/assets/i18n/', suffix: '.json?' + packageJson.version },
        { prefix: '/sb-modules/Frontend-Document-Viewer-Module/assets/i18n/', suffix: '.json?' + packageJson.version },
    ]);
}

function initQuill(quillHelperService: QuillHelperService) {
    return () => quillHelperService.initQuillRegisters();
}

if (environment.production) {
    enableProdMode();
}

bootstrapApplication(AppComponent, {
    providers: [
        provideRouter(APP_ROUTES, withPreloading(CustomPreloadingStrategyService)),
        provideHttpClient(withInterceptorsFromDi()),
        provideAnimations(),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ApiInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ErrorInterceptor,
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initQuill,
            multi: true,
            deps: [QuillHelperService],
        },
        UserService,
        CurrentCategoryResolver,
        CategoriesResolver,
        FieldRuleValidationTypeNamePipe,
        TicketSourcePipe,
        EmailTemplatesTypesPipe,
        SortPipe,
        RedirectGuard,
        CanActivateCommunitySettingsGuard,
        CanActivateWidgetSettingsGuard,
        CanActivateSuperAdminSettingsGuard,
        InitRedirectGuard,
        FacebookRedirectGuard,
        InstagramRedirectGuard,
        GoogleRedirectGuard,
        YoutubeRedirectGuard,
        TrustpilotRedirectGuard,
        TiktokRedirectGuard,
        MicrosoftDynamicsRedirectGuard,
        importProvidersFrom(
            ModalModule.forRoot(),
            TooltipModule.forRoot(),
            BsDatepickerModule.forRoot(),
            PopoverModule.forRoot(),
            UiSwitchModule.forRoot(ngxUiSwitchConfig),
            SimpleNotificationsModule.forRoot({
                position: ['bottom', 'right'],
                timeOut: 3000,
                lastOnBottom: true,
                showProgressBar: true,
                pauseOnHover: true,
            }),
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: HttpLoaderFactory,
                    deps: [HttpClient],
                },
            }),
            NgxMaskModule.forRoot(),
        ),
    ],
}).catch((err) => {
    return console.error(err);
});
